'use client';

import AppImage from '../../ui/AppImage';
import { AppointmentBookingTrigger } from '@/app/components/appointments/AppointmentBookingTrigger';
import { GET_SERVICES } from '@/lib/graphql';
import Icon from '../../ui/AppIcon';
import Link from 'next/link';
import { Service } from '@/lib/types/service.types';
import { motion } from 'framer-motion';
import { stripHtml } from '@/lib/utils/strip-html';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

export default function ServicesSection() {
  const serviceColors = [
    'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
    'bg-pink-50 text-pink-600 dark:bg-pink-900/20 dark:text-pink-400',
    'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400',
    'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400',
    'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400',
  ];

  const {
    data: servicesData,
    error: servicesError,
  } = useQuery<{ services: { data: Service[] } }>(GET_SERVICES, {
    variables: {
      page: 1,
      limit: 6,
      featured: true,
    },
  });

  const services = useMemo<Service[]>(() => {
    if (servicesError) {
      console.error('GraphQL error details:', servicesError);
      return [];
    }
    return servicesData?.services?.data ?? [];
  }, [servicesData, servicesError]);

  return (
    <section id="services" className="py-20 md:py-32 bg-secondary reveal">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            OUR SERVICES
          </h2>
          <p className="text-lg text-muted-foreground">
            Professional natural hair care services tailored to your unique needs and goals.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-12 gap-2 p-2">
          <div className='col-span-4 hidden md:block p-2 relative rounded-3xl overflow-hidden'>
            <AppImage
              src="/office.jpg"
              alt="Mini Twist"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 rounded-3xl" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>
          <div className='col-span-12 md:col-span-8 p-2'>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => {
                const serviceColor = serviceColors[index % serviceColors.length];
                return (
                  <Link key={service.id} href={`/services/${service.slug}`}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      whileHover={{ y: -8 }}
                      className="bg-card rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer border border-border"
                    >
                      {/* Icon */}
                      <div className={`w-16 h-16 rounded-2xl ${serviceColor} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon name="SparklesIcon" size={32} />
                      </div>

                      {/* Content */}
                      <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                        {service.service_name}
                      </h3>
                      <p className="text-muted-foreground mb-6 leading-relaxed break-words line-clamp-2 md:line-clamp-3">
                        {service.service_description
                          ? stripHtml(service.service_description) ||
                          'Professional hair care service tailored to your needs.'
                          : 'Professional hair care service tailored to your needs.'}
                      </p>

                      {/* Arrow Link */}
                      <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all">
                        <span>Learn More</span>
                        <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className="text-muted-foreground mb-6">
            Not sure which service you need? We&apos;re here to help.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link href="/services">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-xl font-semibold shadow-lg shadow-primary/30 transition-all"
              >
                View All Services
              </motion.button>
            </Link>
            <AppointmentBookingTrigger>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border border-primary text-primary hover:bg-primary/10 px-8 py-4 rounded-xl font-semibold transition-all"
              >
                Book Appointment
              </motion.button>
            </AppointmentBookingTrigger>
          </div>
        </motion.div>
      </div>
    </section>
  )
}